// src/components/Editor/EditorLayout.jsx
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useParams, Navigate, useNavigate } from 'react-router-dom';
import EditorUI from './EditorUI';
import useDocsTree from '../../state/useDocsTree';
import { debug } from '../../helpers/debug';
import { getPreference, setPreference } from '../../helpers/preferencesHelpers';
import { runReturnSync, runBackgroundSync } from '../../helpers/syncHelpers';
import { apiTouchProjectViewed, apiAcceptProjectInvite } from '../../api/projects';
import { apiGetDocs } from '../../api/docs';
import { canEditProjectContent, getViewMode } from '../../helpers/projectAccess';
import { ViewModeProvider } from '../../state/ViewModeContext';
import ModalShare from '../Overlays/ModalShare';
import { buildRestoredTabs } from '../../helpers/tabHelpers';

export default function EditorLayout({ user }) {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [currentProject, setCurrentProject] = useState(null);
  const [tabs, setTabs] = useState([]);
  const [activeTabId, setActiveTabId] = useState(null);
  const [leftActiveTabId, setLeftActiveTabId] = useState(null);
  const [rightActiveTabId, setRightActiveTabId] = useState(null);
  const [activePane, setActivePane] = useState('left'); // 'left' or 'right'
  const [hideEditorChrome, setHideEditorChrome] = useState(false);
  const [selectedDocId, setSelectedDocId] = useState(null);
  const [activeEditor, setActiveEditor] = useState(null);
  const [optionsOpen, setOptionsOpen] = useState(false);
  const [tabsRestored, setTabsRestored] = useState(false);
  
  const [activePanel, setActivePanelState] = useState(() => {
    return getPreference(null, 'activePanel', 'projectButton');
  });
  const [projectPinned, setProjectPinned] = useState(() => {
    return getPreference(null, 'projectFixed', true);
  });
  const [outlinePinned, setOutlinePinned] = useState(() => {
    return getPreference(null, 'outlineFixed', false);
  });
  const [versionsPinned, setVersionsPinned] = useState(() => {
    return getPreference(null, 'versionsFixed', false);
  });
  const [chatsPinned, setChatsPinned] = useState(() => {
    return getPreference(null, 'chatsFixed', false);
  });
  const [optionsPinned, setOptionsPinned] = useState(() => {
    return getPreference(null, 'optionsFixed', false);
  });

  const setActivePanel = useCallback((nextPanelOrUpdater) => {
    setActivePanelState((prevPanel) => {
      const nextPanel = typeof nextPanelOrUpdater === 'function'
        ? nextPanelOrUpdater(prevPanel)
        : nextPanelOrUpdater;

      const sharedLeftPanels = ['projectButton', 'chatsButton', 'outlineButton', 'versionsButton'];
      const switchingBetweenPanelWideModes =
        sharedLeftPanels.includes(prevPanel) &&
        sharedLeftPanels.includes(nextPanel) &&
        prevPanel !== nextPanel;

      if (switchingBetweenPanelWideModes) {
        const pinnedToCarry = prevPanel === 'projectButton'
          ? projectPinned
          : prevPanel === 'chatsButton'
          ? chatsPinned
          : prevPanel === 'outlineButton'
          ? outlinePinned
          : versionsPinned;
        setProjectPinned(pinnedToCarry);
        setChatsPinned(pinnedToCarry);
        setOutlinePinned(pinnedToCarry);
        setVersionsPinned(pinnedToCarry);
      }

      return nextPanel;
    });
  }, [projectPinned, chatsPinned, outlinePinned, versionsPinned]);
  const [splitScreen, setSplitScreen] = useState(false);
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);
  const [shareModalOpen, setShareModalOpen] = useState(false);

  const editorPanelRef = useRef();
  const rightEditorPanelRef = useRef();
  const lastViewedEventAtByProjectRef = useRef({});
  const hasPersistedTabsByStorageKeyRef = useRef({});

  // Detect mobile only on initial load. Runtime resize can be transient (e.g., browser tab drag),
  // so avoid mutating layout behavior based on temporary viewport changes.
  useEffect(() => {
    setIsMobile(window.innerWidth < 768);
  }, []);

  // Save splitScreen state per project
  useEffect(() => {
    if (!user || !currentProject) return;
    const storageUserKey = `${user.id}_${currentProject.id}`;
    setPreference(storageUserKey, 'splitScreen', splitScreen);
  }, [splitScreen, user, currentProject]);

  // When closing split screen, move all right pane tabs to left pane
  useEffect(() => {
    if (!splitScreen && tabs.length > 0) {
      const hasRightTabs = tabs.some(t => t.pane === 'right');
      if (hasRightTabs) {
        // Separate left and right tabs, then concatenate with right tabs at the end
        setTabs(prev => {
          const leftTabs = prev.filter(t => !t.pane || t.pane === 'left');
          const rightTabs = prev.filter(t => t.pane === 'right').map(t => ({ ...t, pane: 'left' }));
          return [...leftTabs, ...rightTabs];
        });
        setRightActiveTabId(null);
        // If active pane was right, switch to left
        if (activePane === 'right') {
          setActivePane('left');
        }
      }
    }
  }, [splitScreen, tabs, activePane]);

  // Save panel states
  useEffect(() => {
    setPreference(null, 'activePanel', activePanel);
  }, [activePanel]);

  useEffect(() => {
    setPreference(null, 'projectFixed', projectPinned);
  }, [projectPinned]);

  useEffect(() => {
    setPreference(null, 'outlineFixed', outlinePinned);
  }, [outlinePinned]);

  useEffect(() => {
    setPreference(null, 'versionsFixed', versionsPinned);
  }, [versionsPinned]);

  useEffect(() => {
    setPreference(null, 'chatsFixed', chatsPinned);
  }, [chatsPinned]);

  useEffect(() => {
    setPreference(null, 'optionsFixed', optionsPinned);
  }, [optionsPinned]);

  // Load project when projectId changes
  useEffect(() => {
    if (!projectId || !user) return;

    (async () => {
      try {
        const pid = Number(projectId);

        await apiAcceptProjectInvite(pid, user.id, user.email);

        // Ensure `currentProject` is set immediately to the routed project id so
        // `useDocsTree` doesn't fall back to the user's first project while we
        // asynchronously resolve the full project data. This prevents the tree
        // from loading the wrong project's items on first render.
        setCurrentProject({ id: pid });

        // Always fetch the latest project data to avoid using stale cached data
        // (e.g., after a project rename on the projects page)
        const { apiGetProjects } = await import('../../api/projects');
        const list = await apiGetProjects(user.id, user.email);
        const resolved = (list || []).find(p => Number(p.id) === pid) || null;

        if (resolved) {
          setCurrentProject(resolved);
        }
      } catch (err) {
        debug('[EditorLayout] Failed to load project:', { type: 'error' }, err);
        setCurrentProject({ id: Number(projectId) });
      }
    })();
  }, [projectId, user]);

  // Update document title to show current project name
  useEffect(() => {
    if (currentProject?.name) {
      document.title = `${currentProject.name} | Ghost`;
    } else if (currentProject?.id) {
      document.title = 'Project | Ghost';
    } else {
      document.title = 'Ghost';
    }
    return () => { document.title = 'Ghost'; };
  }, [currentProject?.id, currentProject?.name]);

  // Tree State (docs/folders)
  const {
    itemsArr,
    itemsMap,
    setItemsMap,
    refresh,
    dragStart,
    dragOver,
    dragEnd,
    dropOnItem,
    dropInProject,
    dropInTabs,
    dropInEditor,
    draggedId,
    authorized
  } = useDocsTree({ user, currentProject, tabs, setTabs, setActiveTabId, setLeftActiveTabId, setRightActiveTabId });

  // Redirect to projects page if user is not authorized to access this project
  useEffect(() => {
    // Only redirect when authorized is explicitly false (not null/undefined)
    // null means the check hasn't completed yet
    if (currentProject?.id && authorized === false) {
      console.warn('[EditorLayout] Unauthorized access attempt, redirecting to projects page');
      navigate('/projects', { replace: true });
    }
  }, [authorized, currentProject?.id, navigate]);

  // Update per-user viewed_at when the editor loads a project.
  useEffect(() => {
    if (!user?.id || !currentProject?.id || authorized !== true) return;

    const projectKey = String(currentProject.id);
    const now = Date.now();
    const lastViewedEventAt = lastViewedEventAtByProjectRef.current[projectKey] || 0;
    const minViewEventIntervalMs = 30 * 1000;
    if (now - lastViewedEventAt < minViewEventIntervalMs) return;

    lastViewedEventAtByProjectRef.current[projectKey] = now;
    apiTouchProjectViewed(currentProject.id, user.id).catch(() => {});
  }, [user?.id, currentProject?.id, authorized]);

  // Save tabs to localStorage when they change
  useEffect(() => {
    if (!tabsRestored || !user || !currentProject) return;
    
    const storageUserKey = `${user.id}_${currentProject.id}`;
    if (tabs.length > 0) {
      hasPersistedTabsByStorageKeyRef.current[storageUserKey] = true;
      const tabsToSave = tabs.map(tab => ({
        docId: tab.docId,
        pane: tab.pane || 'left'
      }));
      setPreference(storageUserKey, 'openTabs', tabsToSave);
      
      // Save pane-specific active tabs
      if (leftActiveTabId) {
        const leftTab = tabs.find(t => t.id === leftActiveTabId);
        if (leftTab) setPreference(storageUserKey, 'leftActiveTabDocId', leftTab.docId);
      }
      if (splitScreen && rightActiveTabId) {
        const rightTab = tabs.find(t => t.id === rightActiveTabId);
        if (rightTab) setPreference(storageUserKey, 'rightActiveTabDocId', rightTab.docId);
      } else {
        setPreference(storageUserKey, 'rightActiveTabDocId', null);
      }
    } else if (tabs.length === 0 && tabsRestored && hasPersistedTabsByStorageKeyRef.current[storageUserKey]) {
      // Avoid wiping saved tabs when restore temporarily fails on first load.
      setPreference(storageUserKey, 'openTabs', []);
      setPreference(storageUserKey, 'leftActiveTabDocId', null);
      setPreference(storageUserKey, 'rightActiveTabDocId', null);
    }
  }, [user, currentProject, tabs, leftActiveTabId, rightActiveTabId, splitScreen, tabsRestored]);

  // Universal check: If a pane has tabs but no active tab, activate the first one
  useEffect(() => {
    const leftTabs = tabs.filter(t => !t.pane || t.pane === 'left');
    const rightTabs = tabs.filter(t => t.pane === 'right');
    const leftHasValidActive = Boolean(leftActiveTabId) && leftTabs.some(t => t.id === leftActiveTabId);
    const rightHasValidActive = Boolean(rightActiveTabId) && rightTabs.some(t => t.id === rightActiveTabId);
    
    // If left pane has tabs but no active tab (or stale active id), activate first.
    if (leftTabs.length > 0 && !leftHasValidActive) {
      setLeftActiveTabId(leftTabs[0].id);
    } else if (leftTabs.length === 0 && leftActiveTabId) {
      setLeftActiveTabId(null);
    }
    
    // Same invariant for right pane while split screen is enabled.
    if (splitScreen && rightTabs.length > 0 && !rightHasValidActive) {
      setRightActiveTabId(rightTabs[0].id);
    } else if (splitScreen && rightTabs.length === 0 && rightActiveTabId) {
      setRightActiveTabId(null);
    }
  }, [tabs, leftActiveTabId, rightActiveTabId, splitScreen]);

  // Sync activeTabId with pane-specific active tabs based on activePane
  useEffect(() => {
    const leftTabs = tabs.filter(t => !t.pane || t.pane === 'left');
    const rightTabs = tabs.filter(t => t.pane === 'right');
    let targetTabId = null;

    if (!splitScreen) {
      targetTabId = leftActiveTabId || leftTabs[0]?.id || null;
    } else if (activePane === 'left') {
      targetTabId = leftActiveTabId || leftTabs[0]?.id || rightActiveTabId || rightTabs[0]?.id || null;
    } else {
      targetTabId = rightActiveTabId || rightTabs[0]?.id || leftActiveTabId || leftTabs[0]?.id || null;
    }

    if (!targetTabId && tabs.length > 0) {
      targetTabId = tabs[0].id;
    }

    if (targetTabId !== activeTabId) {
      setActiveTabId(targetTabId || null);
    }
  }, [splitScreen, activePane, leftActiveTabId, rightActiveTabId, tabs, activeTabId]);

  // Sync selectedDocId with activeTabId
  useEffect(() => {
    if (activeTabId) {
      const activeTab = tabs.find(t => t.id === activeTabId);
      if (activeTab && activeTab.docId !== selectedDocId) {
        setSelectedDocId(activeTab.docId);
      }
    } else if (selectedDocId) {
      setSelectedDocId(null);
    }
  }, [activeTabId, tabs, selectedDocId]);

  // Restore tabs after itemsMap is ready
  useEffect(() => {
    if (!user || !currentProject || authorized === null || tabsRestored) return;
    let cancelled = false;

    const restoreTabs = async () => {
      const storageUserKey = `${user.id}_${currentProject.id}`;
      const savedTabs = getPreference(storageUserKey, 'openTabs', []);
      const normalizedSavedTabs = Array.isArray(savedTabs) ? savedTabs : [];

      // If tabs already exist, just mark as restored
      if (tabs && tabs.length > 0) {
        setTabsRestored(true);
        return;
      }

      const savedLeftActiveDocId = getPreference(storageUserKey, 'leftActiveTabDocId', null);
      const savedRightActiveDocId = getPreference(storageUserKey, 'rightActiveTabDocId', null);
      const savedSplitScreen = getPreference(storageUserKey, 'splitScreen', false);

      const hasRightPaneTabs = normalizedSavedTabs.some(tabData => {
        const pane = typeof tabData === 'object' ? tabData.pane : 'left';
        return pane === 'right';
      });
      setSplitScreen(Boolean(savedSplitScreen) || hasRightPaneTabs);

      let fallbackDocs = [];
      if (normalizedSavedTabs.length > 0) {
        try {
          fallbackDocs = await apiGetDocs(currentProject.id);
        } catch (err) {
          fallbackDocs = [];
        }
      }

      if (cancelled) return;

      if (normalizedSavedTabs.length > 0) {
        const restoredTabs = buildRestoredTabs({
          savedTabs: normalizedSavedTabs,
          itemsMap,
          fallbackDocs
        });

        if (restoredTabs.length > 0) {
          setTabs(restoredTabs);

          // Restore left pane active tab
          if (savedLeftActiveDocId) {
            const leftTab = restoredTabs.find(t => t.docId === savedLeftActiveDocId);
            if (leftTab) {
              setLeftActiveTabId(leftTab.id);
              setActiveTabId(leftTab.id);
              setSelectedDocId(leftTab.docId);
            }
          } else {
            // Fallback to first left pane tab
            const firstLeftTab = restoredTabs.find(t => !t.pane || t.pane === 'left');
            if (firstLeftTab) {
              setLeftActiveTabId(firstLeftTab.id);
              setActiveTabId(firstLeftTab.id);
              setSelectedDocId(firstLeftTab.docId);
            }
          }

          // Restore right pane active tab only from right pane tabs
          if (savedRightActiveDocId && hasRightPaneTabs) {
            const rightTab = restoredTabs.find(t => t.pane === 'right' && t.docId === savedRightActiveDocId);
            if (rightTab) setRightActiveTabId(rightTab.id);
            else setRightActiveTabId(null);
          } else {
            setRightActiveTabId(null);
            setActivePane('left');
          }
        }
      }

      setTabsRestored(true);
    };

    void restoreTabs();

    return () => {
      cancelled = true;
    };
  }, [user, currentProject, itemsMap, authorized, tabsRestored, tabs, setTabs]);

  // Event-driven sync orchestration for foreground/background browser lifecycle events.
  useEffect(() => {
    if (!user || !currentProject || !tabsRestored) return;

    const handleWindowFocus = async () => {
      await runReturnSync({
        reason: 'window-focus',
        userId: user.id,
        projectId: currentProject.id,
        tabs,
        leftActiveTabId,
        rightActiveTabId,
        splitScreen,
        leftPanelRef: editorPanelRef,
        rightPanelRef: rightEditorPanelRef,
        refreshProjectTree: refresh,
        isVisible: document.visibilityState === 'visible'
      });
    };

    const handleVisibilityChange = async () => {
      if (document.visibilityState === 'hidden') {
        await runBackgroundSync({
          reason: 'visibility-hidden',
          userId: user.id,
          tabs,
          leftActiveTabId,
          rightActiveTabId,
          splitScreen,
          leftPanelRef: editorPanelRef,
          rightPanelRef: rightEditorPanelRef
        });
        return;
      }
      if (document.visibilityState !== 'visible') return;
      await runReturnSync({
        reason: 'visibility-change',
        userId: user.id,
        projectId: currentProject.id,
        tabs,
        leftActiveTabId,
        rightActiveTabId,
        splitScreen,
        leftPanelRef: editorPanelRef,
        rightPanelRef: rightEditorPanelRef,
        refreshProjectTree: refresh,
        isVisible: true
      });
    };

    const handlePageShow = async () => {
      await runReturnSync({
        reason: 'pageshow',
        userId: user.id,
        projectId: currentProject.id,
        tabs,
        leftActiveTabId,
        rightActiveTabId,
        splitScreen,
        leftPanelRef: editorPanelRef,
        rightPanelRef: rightEditorPanelRef,
        refreshProjectTree: refresh,
        isVisible: document.visibilityState === 'visible'
      });
    };

    const handleWindowBlur = async () => {
      await runBackgroundSync({
        reason: 'window-blur',
        userId: user.id,
        tabs,
        leftActiveTabId,
        rightActiveTabId,
        splitScreen,
        leftPanelRef: editorPanelRef,
        rightPanelRef: rightEditorPanelRef
      });
    };

    const handleMouseOut = async (event) => {
      const nextTarget = event?.relatedTarget || event?.toElement;
      if (nextTarget) return;
      await runBackgroundSync({
        reason: 'window-mouse-leave',
        userId: user.id,
        tabs,
        leftActiveTabId,
        rightActiveTabId,
        splitScreen,
        leftPanelRef: editorPanelRef,
        rightPanelRef: rightEditorPanelRef
      });
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleWindowFocus);
    window.addEventListener('pageshow', handlePageShow);
    window.addEventListener('blur', handleWindowBlur);
    window.addEventListener('mouseout', handleMouseOut);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleWindowFocus);
      window.removeEventListener('pageshow', handlePageShow);
      window.removeEventListener('blur', handleWindowBlur);
      window.removeEventListener('mouseout', handleMouseOut);
    };
  }, [user, currentProject, tabsRestored, tabs, leftActiveTabId, rightActiveTabId, splitScreen, refresh]);

  const handleToggleOptions = () => setOptionsOpen(prev => !prev);

  const handleClickOutside = (e) => {
    const isLeftPanel = ['projectButton', 'outlineButton', 'versionsButton', 'chatsButton'].includes(activePanel);
    const leftPanelFixed = !isMobile && (
      activePanel === 'projectButton'
        ? projectPinned
        : activePanel === 'chatsButton'
        ? chatsPinned
        : activePanel === 'outlineButton'
        ? outlinePinned
        : activePanel === 'versionsButton'
        ? versionsPinned
        : false
    );
    const clickedInsideEditor = e.target.closest('#editorContainer');
    const clickedInsideEditorChrome =
      clickedInsideEditor ||
      e.target.closest('[id^="toolbar"]') ||
      e.target.closest('.chatComposer');
    setHideEditorChrome(!clickedInsideEditorChrome);

    if (isLeftPanel && !leftPanelFixed) {
      const shouldKeepOutlineOpen = activePanel === 'outlineButton' && clickedInsideEditor;
      if (!e.target.closest('#leftPanel') && !e.target.closest('#nav') && !shouldKeepOutlineOpen) {
        setActivePanel(null);
      }
    }
    
    if (optionsOpen && (isMobile || !optionsPinned)) {
      if (!e.target.closest('#optionsPanel') && !e.target.closest('#tabShelf')) {
        setOptionsOpen(false);
      }
    }
  };

  // Redirect to auth if no user
  if (!user) {
    return <Navigate to="/auth" replace />;
  }

  const projectReadOnly = currentProject ? !canEditProjectContent(currentProject) : false;
  const viewMode = currentProject ? getViewMode(currentProject) : 'editor';

  return (
    <ViewModeProvider viewMode={viewMode}>
    <EditorUI
      user={user}
      currentProject={currentProject}
      projectReadOnly={projectReadOnly}
      activePanel={activePanel}
      setActivePanel={setActivePanel}
      isMobile={isMobile}
      projectPinned={projectPinned}
      outlinePinned={outlinePinned}
      versionsPinned={versionsPinned}
      chatsPinned={chatsPinned}
      setProjectPinned={setProjectPinned}
      setOutlinePinned={setOutlinePinned}
      setVersionsPinned={setVersionsPinned}
      setChatsPinned={setChatsPinned}
      tabs={tabs}
      setTabs={setTabs}
      activeTabId={activeTabId}
      setActiveTabId={setActiveTabId}
      selectedDocId={selectedDocId}
      setSelectedDocId={setSelectedDocId}
      editorPanelRef={editorPanelRef}
      rightEditorPanelRef={rightEditorPanelRef}
      onEditorChange={setActiveEditor}
      optionsOpen={optionsOpen}
      handleToggleOptions={handleToggleOptions}
      optionsPinned={optionsPinned}
      setOptionsPinned={setOptionsPinned}
      itemsArr={itemsArr}
      itemsMap={itemsMap}
      setItemsMap={setItemsMap}
      dragStart={dragStart}
      dragOver={dragOver}
      dragEnd={dragEnd}
      dropOnItem={dropOnItem}
      dropInProject={dropInProject}
      dropInTabs={dropInTabs}
      dropInEditor={dropInEditor}
      draggedId={draggedId}
      handleClickOutside={handleClickOutside}
      activeEditor={activeEditor}
      splitScreen={splitScreen}
      setSplitScreen={setSplitScreen}
      leftActiveTabId={leftActiveTabId}
      setLeftActiveTabId={setLeftActiveTabId}
      rightActiveTabId={rightActiveTabId}
      setRightActiveTabId={setRightActiveTabId}
      activePane={activePane}
      setActivePane={setActivePane}
      revealEditorChrome={() => setHideEditorChrome(false)}
      hideEditorChrome={hideEditorChrome}
      tabsRestored={tabsRestored}
      refresh={refresh}
      showShareButton={!projectReadOnly}
      onShareClick={() => setShareModalOpen(true)}
    />
    {currentProject && !projectReadOnly && (
      <ModalShare
        isOpen={shareModalOpen}
        onClose={() => setShareModalOpen(false)}
        linkType="project"
        targetId={currentProject.id}
        projectId={currentProject.id}
        visualState={{
          openTabs: tabs.map(t => ({ docId: t.docId, pane: t.pane || 'left' })),
          activePane,
          splitScreen
        }}
        user={user}
      />
    )}
    </ViewModeProvider>
  );
}
